import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { availabilityService } from '../../../services/availabilityService';
import { sessionService } from '../../../services/sessionService';
import { supabase } from '../../../lib/supabase';

const SessionScheduler = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('day');
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [allAvailabilitySlots, setAllAvailabilitySlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);

  // New session form state
  const [newSession, setNewSession] = useState({
    studentId: '',
    sessionDate: '',
    sessionTime: '09:00',
    durationMinutes: 30,
    sessionType: 'one_on_one',
    notes: ''
  });

  // New availability form state
  const [newSlot, setNewSlot] = useState({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '10:00',
    maxCounselors: 1
  });

  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'
  ];

  // Load sessions from database
  useEffect(() => {
    if (user?.id) {
      loadSessions();
      loadAllAvailability();
    }
  }, [user?.id]);

  // Load students for dropdown
  useEffect(() => {
    if (showNewSessionModal) {
      loadStudents();
    }
  }, [showNewSessionModal]);

  const loadSessions = async () => {
    try {
      const data = await sessionService?.getSessions(user?.id, 'institute');
      setSessions(data || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const loadAllAvailability = async () => {
    try {
      const slots = await availabilityService?.getAvailability(user?.id);
      setAllAvailabilitySlots(slots || []);
    } catch (err) {
      console.error('Error loading all availability:', err);
    }
  };

  const loadStudents = async () => {
    try {
      const { data, error } = await supabase
        ?.from('users')
        ?.select('id, name, email')
        ?.eq('role', 'student')
        ?.order('name');
      if (error) throw error;
      setStudents(data || []);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students');
    }
  };

  const handleCreateSession = async () => {
    if (!newSession?.studentId || !newSession?.sessionDate || !newSession?.sessionTime) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const scheduledAt = `${newSession?.sessionDate}T${newSession?.sessionTime}:00`;
      await sessionService?.createSession({
        studentId: newSession?.studentId,
        instituteId: user?.id,
        sessionType: newSession?.sessionType,
        scheduledAt,
        durationMinutes: newSession?.durationMinutes,
        meetingUrl: `/institute-video-session?sessionId=new`
      });
      setNewSession({
        studentId: '',
        sessionDate: '',
        sessionTime: '09:00',
        durationMinutes: 30,
        sessionType: 'one_on_one',
        notes: ''
      });
      setShowNewSessionModal(false);
      await loadSessions();
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': case 'scheduled': return 'bg-success text-success-foreground';
      case 'pending': case 'ongoing': return 'bg-warning text-warning-foreground';
      case 'cancelled': return 'bg-error text-error-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    return status?.charAt(0)?.toUpperCase() + status?.slice(1);
  };

  const getDayLabel = (dayOfWeek) => {
    return daysOfWeek?.find((d) => d?.value === dayOfWeek)?.label || 'Unknown';
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatSessionTime = (scheduledAt) => {
    if (!scheduledAt) return '';
    const date = new Date(scheduledAt);
    const hours = date?.getHours();
    const minutes = date?.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes?.toString()?.padStart(2, '0')} ${ampm}`;
  };

  // Load availability slots (for modal)
  useEffect(() => {
    if (user?.id && showAvailabilityModal) {
      loadAvailability();
    }
  }, [user?.id, showAvailabilityModal]);

  const loadAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      const slots = await availabilityService?.getAvailability(user?.id);
      setAvailabilitySlots(slots || []);
      setAllAvailabilitySlots(slots || []);
    } catch (err) {
      console.error('Error loading availability:', err);
      setError('Failed to load availability slots');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async () => {
    if (!newSlot?.startTime || !newSlot?.endTime) {
      setError('Please select both start and end times');
      return;
    }
    if (newSlot?.startTime >= newSlot?.endTime) {
      setError('End time must be after start time');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await availabilityService?.createAvailability({
        instituteId: user?.id,
        dayOfWeek: newSlot?.dayOfWeek,
        startTime: newSlot?.startTime,
        endTime: newSlot?.endTime,
        maxCounselors: newSlot?.maxCounselors ?? 1
      });
      setNewSlot({ dayOfWeek: 1, startTime: '09:00', endTime: '10:00', maxCounselors: 1 });
      await loadAvailability();
    } catch (err) {
      console.error('Error adding slot:', err);
      setError('Failed to add availability slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      setLoading(true);
      setError(null);
      await availabilityService?.deleteAvailability(slotId);
      await loadAvailability();
    } catch (err) {
      console.error('Error deleting slot:', err);
      setError('Failed to delete availability slot');
    } finally {
      setLoading(false);
    }
  };

  // ─── Calendar helpers ───────────────────────────────────────────────────────

  const isSameDay = (a, b) =>
    a?.getFullYear() === b?.getFullYear() &&
    a?.getMonth() === b?.getMonth() &&
    a?.getDate() === b?.getDate();

  const isToday = (date) => isSameDay(date, new Date());

  /** Returns true if the given calendar date has at least one scheduled session */
  const hasSession = (date) => {
    return sessions?.some((s) => {
      if (!s?.scheduledAt) return false;
      return isSameDay(new Date(s.scheduledAt), date);
    });
  };

  /** Returns true if the given calendar date falls on a day with availability slots */
  const hasAvailability = (date) => {
    const dow = date?.getDay(); // 0=Sun … 6=Sat
    return allAvailabilitySlots?.some((slot) => slot?.dayOfWeek === dow);
  };

  /** Returns sessions for the currently selected date */
  const sessionsForSelectedDate = sessions?.filter((s) =>
    isSameDay(new Date(s.scheduledAt), selectedDate)
  );

  /** Returns availability slots for the currently selected date */
  const availabilityForSelectedDate = allAvailabilitySlots?.filter(
    (slot) => slot?.dayOfWeek === selectedDate?.getDay()
  );

  /** Build calendar grid for calendarMonth */
  const buildCalendarDays = () => {
    const year = calendarMonth?.getFullYear();
    const month = calendarMonth?.getMonth();
    const firstDay = new Date(year, month, 1)?.getDay();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    const days = [];
    // leading empty cells
    for (let i = 0; i < firstDay; i++) days?.push(null);
    for (let d = 1; d <= daysInMonth; d++) days?.push(new Date(year, month, d));
    return days;
  };

  const calendarDays = buildCalendarDays();

  const monthLabel = calendarMonth?.toLocaleString('default', { month: 'long', year: 'numeric' });

  const prevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1));
  };

  const formatSelectedDate = (date) =>
    date?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // ────────────────────────────────────────────────────────────────────────────

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="border-b border-border p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                Session Scheduler
              </h2>
              <p className="text-sm text-muted-foreground">
                <span className="data-text">{sessions?.length}</span> sessions scheduled
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              iconPosition="left"
              onClick={() => setShowAvailabilityModal(true)}>
              Availability
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => setShowNewSessionModal(true)}>
              New Session
            </Button>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-6">
        {/* ── Mini Calendar ─────────────────────────────────────────────────── */}
        <div className="mb-6 bg-muted/30 border border-border rounded-xl p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={prevMonth}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="text-sm font-semibold text-foreground">{monthLabel}</span>
            <button
              onClick={nextMonth}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']?.map((d) => (
              <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Date cells */}
          <div className="grid grid-cols-7 gap-y-1">
            {calendarDays?.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} />;

              const selected = isSameDay(date, selectedDate);
              const today = isToday(date);
              const sessionDay = hasSession(date);
              const availDay = hasAvailability(date);

              return (
                <button
                  key={date?.toISOString()}
                  onClick={() => setSelectedDate(date)}
                  className={`
                    relative flex flex-col items-center justify-center rounded-lg py-1.5 mx-0.5
                    text-xs font-medium transition-all duration-150
                    ${selected
                      ? 'bg-primary text-white shadow-md'
                      : today
                      ? 'bg-primary/10 text-primary font-bold' :'hover:bg-muted text-foreground'}
                  `}>
                  <span>{date?.getDate()}</span>
                  {/* Indicator dots */}
                  {(sessionDay || availDay) && (
                    <div className="flex gap-0.5 mt-0.5">
                      {sessionDay && (
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-white' : 'bg-primary'}`}
                          title="Session scheduled"
                        />
                      )}
                      {availDay && (
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${selected ? 'bg-green-200' : 'bg-green-500'}`}
                          title="Available slot"
                        />
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />
              <span className="text-xs text-muted-foreground">Session scheduled</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
              <span className="text-xs text-muted-foreground">Available slot</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary/20 border border-primary inline-block" />
              <span className="text-xs text-muted-foreground">Today</span>
            </div>
          </div>
        </div>

        {/* ── Selected date header ───────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="px-4 py-2 bg-muted rounded-lg">
              <p className="text-sm font-semibold text-foreground">
                {formatSelectedDate(selectedDate)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'day' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('day')}>
              Day
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}>
              Week
            </Button>
          </div>
        </div>

        {/* ── Availability highlights for selected date ─────────────────────── */}
        {availabilityForSelectedDate?.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block flex-shrink-0" />
              <span className="text-sm font-semibold text-green-800">
                Available Slots on {selectedDate?.toLocaleDateString('en-US', { weekday: 'long' })}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {availabilityForSelectedDate?.map((slot) => (
                <span
                  key={slot?.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 border border-green-300 text-green-800 text-xs font-medium rounded-full">
                  <Icon name="Clock" size={11} />
                  {formatTime(slot?.startTime)} – {formatTime(slot?.endTime)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Sessions for selected date ─────────────────────────────────────── */}
        <div className="space-y-4">
          {sessionsForSelectedDate?.length === 0 && sessions?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Icon name="Calendar" size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No sessions scheduled</p>
              <p className="text-sm mt-1">Click "New Session" to schedule a video counseling session</p>
            </div>
          ) : sessionsForSelectedDate?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="CalendarX" size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No sessions on this day</p>
              <p className="text-xs mt-1">Select another date or schedule a new session</p>
            </div>
          ) : (
            sessionsForSelectedDate?.map((session) => (
              <div key={session?.id} className="p-4 bg-primary/5 border border-primary/20 rounded-lg hover:bg-primary/10 transition-smooth">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-16 text-center">
                    <p className="text-sm font-semibold text-primary data-text">
                      {formatSessionTime(session?.scheduledAt)}
                    </p>
                    <p className="text-xs text-muted-foreground">{session?.durationMinutes} min</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">
                          {session?.sessionType === 'one_on_one' ? 'One-on-One Counseling' : 'Group Presentation'}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{session?.student?.name || 'Student'}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusColor(session?.status)}`}>
                        {getStatusLabel(session?.status)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                      <Icon name="Video" size={14} />
                      <span>Video Counseling</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Video"
                        iconPosition="left"
                        onClick={() => navigate(`/institute-video-session?sessionId=${session?.id}`)}>
                        Join Session
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={() => navigate('/chat-interface')}>
                        Chat
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Calendar"
                        iconPosition="left">
                        Reschedule
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Show all sessions summary if viewing a day with no sessions but sessions exist */}
          {sessionsForSelectedDate?.length === 0 && sessions?.length > 0 && (
            <div className="mt-2 p-3 bg-muted/50 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground text-center">
                You have <span className="font-semibold text-foreground">{sessions?.length}</span> session{sessions?.length !== 1 ? 's' : ''} total — highlighted on the calendar above
              </p>
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" />
            <div className="flex-1">
              <h4 className="font-semibold text-foreground mb-1">Set Your Availability</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Configure your available time slots to allow students to book counseling sessions
              </p>
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={() => setShowAvailabilityModal(true)}>
                Manage Availability
              </Button>
            </div>
          </div>
        </div>

        {/* Model B info card */}
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="CheckCircle" size={18} color="#16a34a" />
            </div>
            <div>
              <h4 className="font-semibold text-green-900 mb-1">Model B Active — Instant Booking</h4>
              <p className="text-sm text-green-800">
                Students can only book from your availability slots above. Sessions are <strong>auto-confirmed</strong> — no back and forth needed.
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-green-700">
                <span className="flex items-center gap-1"><Icon name="Check" size={12} /> You set the slots</span>
                <span className="flex items-center gap-1"><Icon name="Check" size={12} /> Student picks a slot</span>
                <span className="flex items-center gap-1"><Icon name="Check" size={12} /> Auto-confirmed instantly</span>
                <span className="flex items-center gap-1"><Icon name="Users" size={12} /> Multiple counselors per slot</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* New Session Modal */}
      {showNewSessionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  Schedule New Session
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a new video counseling session with a student
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { setShowNewSessionModal(false); setError(null); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="p-4 md:p-6">
              {error && (
                <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Student <span className="text-error">*</span>
                  </label>
                  <select
                    value={newSession?.studentId}
                    onChange={(e) => setNewSession({ ...newSession, studentId: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Choose a student...</option>
                    {students?.map((student) => (
                      <option key={student?.id} value={student?.id}>
                        {student?.name} ({student?.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Session Date <span className="text-error">*</span>
                  </label>
                  <input
                    type="date"
                    value={newSession?.sessionDate}
                    onChange={(e) => setNewSession({ ...newSession, sessionDate: e?.target?.value })}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Session Time <span className="text-error">*</span>
                  </label>
                  <select
                    value={newSession?.sessionTime}
                    onChange={(e) => setNewSession({ ...newSession, sessionTime: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    {timeSlots?.map((time) => (
                      <option key={time} value={time}>{formatTime(time)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Duration</label>
                  <select
                    value={newSession?.durationMinutes}
                    onChange={(e) => setNewSession({ ...newSession, durationMinutes: parseInt(e?.target?.value) })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Session Type</label>
                  <select
                    value={newSession?.sessionType}
                    onChange={(e) => setNewSession({ ...newSession, sessionType: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="one_on_one">One-on-One Counseling</option>
                    <option value="group_presentation">Group Presentation</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Notes (Optional)</label>
                  <textarea
                    value={newSession?.notes}
                    onChange={(e) => setNewSession({ ...newSession, notes: e?.target?.value })}
                    placeholder="Add any notes about this session..."
                    rows={3}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowNewSessionModal(false); setError(null); }}>
                Cancel
              </Button>
              <Button
                variant="default"
                iconName="Calendar"
                iconPosition="left"
                onClick={handleCreateSession}
                disabled={loading}>
                {loading ? 'Creating...' : 'Create Session'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Availability Modal */}
      {showAvailabilityModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                  Manage Availability
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Set your available time slots for video counseling sessions
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAvailabilityModal(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="p-4 md:p-6">
              {error && (
                <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
                  {error}
                </div>
              )}
              <div className="mb-6 p-4 bg-muted rounded-lg">
                <h4 className="font-semibold text-foreground mb-4">Add New Time Slot</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Day of Week</label>
                    <select
                      value={newSlot?.dayOfWeek}
                      onChange={(e) => setNewSlot({ ...newSlot, dayOfWeek: parseInt(e?.target?.value) })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                      {daysOfWeek?.map((day) => (
                        <option key={day?.value} value={day?.value}>{day?.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Counselors Available
                      <span className="ml-1 text-xs text-muted-foreground font-normal">(simultaneous bookings)</span>
                    </label>
                    <select
                      value={newSlot?.maxCounselors}
                      onChange={(e) => setNewSlot({ ...newSlot, maxCounselors: parseInt(e?.target?.value) })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]?.map((n) => (
                        <option key={n} value={n}>{n} counselor{n !== 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Start Time</label>
                    <select
                      value={newSlot?.startTime}
                      onChange={(e) => setNewSlot({ ...newSlot, startTime: e?.target?.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                      {timeSlots?.map((time) => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">End Time</label>
                    <select
                      value={newSlot?.endTime}
                      onChange={(e) => setNewSlot({ ...newSlot, endTime: e?.target?.value })}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                      {timeSlots?.map((time) => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {newSlot?.maxCounselors > 1 && (
                  <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                    <Icon name="Users" size={14} color="#2563eb" />
                    <p className="text-xs text-blue-800">
                      <span className="font-semibold">{newSlot?.maxCounselors} students</span> can book this slot simultaneously — one per counselor.
                    </p>
                  </div>
                )}
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleAddSlot}
                  disabled={loading}
                  className="mt-4">
                  {loading ? 'Adding...' : 'Add Time Slot'}
                </Button>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-4">
                  Current Availability ({availabilitySlots?.length} slots)
                </h4>
                {loading && availabilitySlots?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
                    Loading availability...
                  </div>
                ) : availabilitySlots?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Calendar" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No availability slots set yet</p>
                    <p className="text-sm mt-1">Add your first time slot above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availabilitySlots?.map((slot) => (
                      <div
                        key={slot?.id}
                        className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-smooth">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-green-100 border border-green-300 rounded-lg flex items-center justify-center">
                            <Icon name="Clock" size={16} color="#16a34a" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{getDayLabel(slot?.dayOfWeek)}</p>
                            <p className="text-sm text-green-700">
                              {formatTime(slot?.startTime)} – {formatTime(slot?.endTime)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full">
                            <Icon name="Users" size={12} color="#2563eb" />
                            <span className="text-xs font-semibold text-blue-700">
                              {slot?.maxCounselors ?? 1} counselor{(slot?.maxCounselors ?? 1) !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSlot(slot?.id)}
                            disabled={loading}>
                            <Icon name="Trash2" size={16} className="text-error" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-6 flex justify-end">
              <Button variant="default" onClick={() => setShowAvailabilityModal(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionScheduler;